import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MultiStepFormComponent, Step } from '../../../../../components/multi-step-form/multi-step-form.component';
import { SetupPhasesStepComponent } from './steps/setup-phases-step/setup-phases-step.component';
import { SetupMilestonesStepComponent } from './steps/setup-milestones-step/setup-milestones-step.component';
import { SetupTaskPackagesStepComponent } from './steps/setup-task-packages-step/setup-task-packages-step.component';
import { SetupSummaryStepComponent } from './steps/setup-summary-step/setup-summary-step.component';
import { ConfigService } from '../../../../../services/config.service';
import { AppGlobals } from '../../../../../app.globals';
import { Phase } from '../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-wizard',
    imports: [
        CommonModule,
        MultiStepFormComponent,
        SetupPhasesStepComponent,
        SetupMilestonesStepComponent,
        SetupTaskPackagesStepComponent,
        SetupSummaryStepComponent,
    ],
    templateUrl: './setup-wizard.component.html',
    styleUrl: './setup-wizard.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupWizardComponent {
  steps: Step[] = [];
  @Output() onComplete = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<any>();

  finishText = $localize`:@@configureProject:Configure Project`;

  phases: Phase[] = [
    {
      id: -1,
      title: 'Project Development',
      showInGantt: true,
      milestones: [
        {
          id: -1,
          name: 'Milestone 1',
          weight: 50,
          taskPackages: [
            {
              id: -1,
              name: 'Task Package 1',
            },
            {
              id: -2,
              name: 'Task Package 2',
            },
          ],
        },
      ],
    },
    {
      id: -2,
      title: 'Project Realization',
      showInGantt: true,
      milestones: [
        {
          id: -2,
          name: 'Milestone 2',
          weight: 50,
          taskPackages: [],
        },
      ],
    },
  ];

  @ViewChild('phasesTemplate')
  phasesTemplate!: TemplateRef<any>;
  @ViewChild('milestonesTemplate')
  milestonesTemplate!: TemplateRef<any>;
  @ViewChild('taskPackagesTemplate')
  taskPackagesTemplate!: TemplateRef<any>;
  @ViewChild('summaryTemplate')
  summaryTemplate!: TemplateRef<any>;
  @ViewChild(MultiStepFormComponent)
  multiStepForm!: MultiStepFormComponent;

  constructor(
    private cdr: ChangeDetectorRef,
    private configService: ConfigService
  ) {}

  ngAfterViewInit() {
    // Initialize steps after view is ready and templates are available
    this.steps = [
      {
        id: 'phases',
        title: $localize`:@@phases:Phases`,
        icon: 'route',
        template: this.phasesTemplate,
        description: $localize`:@@definePhasesForProject:Define the phases for your project.`,
      },
      {
        id: 'milestones',
        title: $localize`:@@milestones:Milestones`,
        icon: 'flag',
        template: this.milestonesTemplate,
        description: $localize`:@@milestonesDescription:Configure milestones for your project.`,
      },
      {
        id: 'task-packages',
        title: $localize`:@@taskPackages:Task Packages`,
        icon: 'folder-library',
        template: this.taskPackagesTemplate,
        description: $localize`:@@taskPackagesDescription:Configure task packages for your project.`,
      },
      {
        id: 'summary',
        title: $localize`:@@summary:Summary`,
        icon: 'task-done',
        template: this.summaryTemplate,
        description: $localize`:@@summaryDescription:Review your project configuration.`,
      },
    ];

    // Manually trigger change detection since we're using OnPush
    this.cdr.detectChanges();
  }

  onPhasesChange = (updatedPhases: Phase[]) => {
    this.phases = updatedPhases;
    this.cdr.detectChanges();
  };

  async onFinishClicked() {
    try {
      const result = await this.configService.saveProjectSetup({
        phases: this.phases,
      });
      if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        console.log('finished setup');

        this.onComplete.emit(result.projectSetup);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  onCancelClicked() {
    this.onCancel.emit();
  }
}
